/**
 * Files Service
 *
 * File upload and management (photos, documents, signatures).
 */

import apiClient, { APIResponse, unwrapResponse } from './apiClient';
import { ENDPOINTS, UPLOAD_LIMITS } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export type FileCategory =
  | 'condition_photo'
  | 'product_document'
  | 'signature'
  | 'contract_document'
  | 'invoice_attachment'
  | 'other';

export interface UploadedFile {
  id: string;
  filename: string;
  original_filename: string;
  content_type: string;
  size: number;
  category: FileCategory;
  url: string;
  presigned_url: string;
  entity_type?: string;
  entity_id?: string;
  description?: string;
  uploaded_at: string;
  uploaded_by: string;
}

export interface FileUploadOptions {
  category?: FileCategory;
  entityType?: string;
  entityId?: string;
  description?: string;
  onProgress?: (progress: number) => void;
}

export interface PresignedUrl {
  file_id: string;
  presigned_url: string;
  expires_at: string;
}

// =============================================================================
// Validation
// =============================================================================

function validateFile(file: File, allowedTypes: readonly string[]): void {
  // Check file size
  if (file.size > UPLOAD_LIMITS.MAX_SIZE_BYTES) {
    throw new Error(`File size exceeds ${UPLOAD_LIMITS.MAX_SIZE_MB}MB limit`);
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`File type "${file.type}" is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
  }
}

function validateImageFile(file: File): void {
  validateFile(file, UPLOAD_LIMITS.ALLOWED_IMAGE_TYPES);
}

function validateDocumentFile(file: File): void {
  validateFile(file, [...UPLOAD_LIMITS.ALLOWED_IMAGE_TYPES, ...UPLOAD_LIMITS.ALLOWED_DOCUMENT_TYPES]);
}

// =============================================================================
// Files Service
// =============================================================================

export const filesService = {
  /**
   * Upload a generic file
   */
  async upload(file: File, options: FileUploadOptions = {}): Promise<UploadedFile> {
    validateDocumentFile(file);

    const formData = new FormData();
    formData.append('file', file);

    if (options.category) {
      formData.append('category', options.category);
    }
    if (options.entityType) {
      formData.append('entity_type', options.entityType);
    }
    if (options.entityId) {
      formData.append('entity_id', options.entityId);
    }
    if (options.description) {
      formData.append('description', options.description);
    }

    const response = await apiClient.post<APIResponse<UploadedFile>>(
      ENDPOINTS.FILES,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: options.onProgress
          ? (progressEvent) => {
              const total = progressEvent.total || 0;
              const progress = total > 0 ? Math.round((progressEvent.loaded * 100) / total) : 0;
              options.onProgress!(progress);
            }
          : undefined,
      }
    );

    return unwrapResponse(response);
  },

  /**
   * Upload a condition report photo
   */
  async uploadConditionPhoto(
    file: File,
    etatDesLieuxId: string,
    options: { description?: string; ordre?: number; onProgress?: (progress: number) => void } = {}
  ): Promise<UploadedFile> {
    validateImageFile(file);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('etat_des_lieux_id', etatDesLieuxId);

    if (options.description) {
      formData.append('description', options.description);
    }
    if (options.ordre !== undefined) {
      formData.append('ordre', options.ordre.toString());
    }

    const response = await apiClient.post<APIResponse<UploadedFile>>(
      `${ENDPOINTS.FILES}/condition-photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: options.onProgress
          ? (progressEvent) => {
              const total = progressEvent.total || 0;
              const progress = total > 0 ? Math.round((progressEvent.loaded * 100) / total) : 0;
              options.onProgress!(progress);
            }
          : undefined,
      }
    );

    return unwrapResponse(response);
  },

  /**
   * Upload a product document
   */
  async uploadProductDocument(
    file: File,
    productId: string,
    documentType: string,
    options: { description?: string; onProgress?: (progress: number) => void } = {}
  ): Promise<UploadedFile> {
    validateDocumentFile(file);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('product_id', productId);
    formData.append('document_type', documentType);

    if (options.description) {
      formData.append('description', options.description);
    }

    const response = await apiClient.post<APIResponse<UploadedFile>>(
      `${ENDPOINTS.FILES}/product-document`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: options.onProgress
          ? (progressEvent) => {
              const total = progressEvent.total || 0;
              const progress = total > 0 ? Math.round((progressEvent.loaded * 100) / total) : 0;
              options.onProgress!(progress);
            }
          : undefined,
      }
    );

    return unwrapResponse(response);
  },

  /**
   * Upload a signature (base64 data URL)
   */
  async uploadSignature(
    dataUrl: string,
    entityType: string,
    entityId: string,
    signatureType: 'client' | 'agent' | 'driver'
  ): Promise<UploadedFile> {
    // Convert data URL to Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // Create file from blob
    const file = new File([blob], `signature_${signatureType}_${Date.now()}.png`, {
      type: 'image/png',
    });

    return this.upload(file, {
      category: 'signature',
      entityType,
      entityId,
      description: `${signatureType} signature`,
    });
  },

  /**
   * List files for an entity
   */
  async listFiles(params: {
    entityType?: string;
    entityId?: string;
    category?: FileCategory;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ files: UploadedFile[]; total: number }> {
    const response = await apiClient.get<APIResponse<{ files: UploadedFile[]; total: number }>>(
      ENDPOINTS.FILES,
      { params }
    );
    return unwrapResponse(response);
  },

  /**
   * Get a presigned URL for file access
   */
  async getPresignedUrl(fileId: string): Promise<PresignedUrl> {
    const response = await apiClient.get<APIResponse<PresignedUrl>>(`${ENDPOINTS.FILES}/${fileId}`);
    return unwrapResponse(response);
  },

  /**
   * Delete a file
   */
  async delete(fileId: string): Promise<void> {
    await apiClient.delete(`${ENDPOINTS.FILES}/${fileId}`);
  },

  /**
   * Download a file
   */
  async download(fileId: string, filename?: string): Promise<void> {
    const { presigned_url } = await this.getPresignedUrl(fileId);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = presigned_url;
    link.download = filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  /**
   * Get file as Blob (for preview)
   */
  async getFileBlob(fileId: string): Promise<Blob> {
    const { presigned_url } = await this.getPresignedUrl(fileId);
    const response = await fetch(presigned_url);
    return response.blob();
  },

  /**
   * Utility: Convert File to base64 data URL
   */
  fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  /**
   * Utility: Check if file type is image
   */
  isImageFile(file: File): boolean {
    return (UPLOAD_LIMITS.ALLOWED_IMAGE_TYPES as readonly string[]).includes(file.type);
  },

  /**
   * Utility: Check if file type is PDF
   */
  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  },
};

export default filesService;
